// /mercado-cliente — "Precios y demanda": la vista de mercado para el cliente
// dueño de propiedades. Muestra un CALENDARIO de demanda por propiedad (pasado
// + futuro). NO usa chart.js (import estático en App.tsx). El admin que entre
// acá es redirigido a /mercado (su versión completa incluye este calendario en
// el tab "Vista cliente"). Ver docs/modulo-mercado.md.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Alert, Empty, Select, Skeleton } from "antd";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import { useAuth } from "../../context/AuthContext";
import { useMercadoChanged } from "../../hooks/useMercadoChanged";
import { isAdminRole } from "../../utils/roles.utils";
import { mercadoApi, ResumenClienteResponse } from "../../services/mercado.api";
import { formatPropertyName } from "../../utils/propertyDisplay";
import MercadoCalendario from "./MercadoCalendario";
import "./mercado.css";

const MercadoClientePage: React.FC = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ResumenClienteResponse | null>(null);
  const [propId, setPropId] = useState<number | null>(null);
  // Sube cuando hay datos nuevos para el calendario visible (refresco silencioso).
  const [refreshToken, setRefreshToken] = useState(0);

  const cargar = useCallback(async () => {
    const res = await mercadoApi.getResumenCliente();
    if (res) {
      setData(res);
      setPropId((prev) => prev ?? res.propiedades[0]?.id ?? null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Realtime, agrupado en hooks/useMercadoChanged (round 2026-09-11). La LISTA
  // de propiedades visibles solo cambia con vínculo / visibilidad / config /
  // borrado; el CALENDARIO que el cliente está mirando cambia además con cada
  // ingesta y con los avisos. Antes cada MERCADO_CHANGED —uno por bloque de un
  // envío del scraper— volvía a pedir /cliente/resumen (que analiza cada
  // propiedad) y aun así el calendario visible se quedaba con los datos viejos.
  useMercadoChanged((c) => {
    if (c.mapeo || c.visibilidad || c.config || c.borrado) cargar();
    if (c.filas || c.notas || c.config || c.mapeo || c.borrado) {
      setRefreshToken((k) => k + 1);
    }
  });

  const opciones = useMemo(
    () =>
      (data?.propiedades ?? []).map((p) => ({
        value: p.id,
        label: formatPropertyName(p.nombre) || `#${p.id}`,
      })),
    [data],
  );

  // Propiedad efectiva: la elegida si sigue disponible, si no la primera.
  const activa = useMemo(() => {
    const props = data?.propiedades ?? [];
    return props.find((p) => p.id === propId) ?? props[0] ?? null;
  }, [data, propId]);

  // El admin tiene su propia página (con este mismo calendario como preview).
  if (isAdminRole(user?.rol)) {
    return <Navigate to="/mercado" replace />;
  }

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="home-content"
    >
      <section className="content-section">
        <PageHeader
          title="Precios y demanda"
          subtitle="Cómo se mueve el mercado para tus propiedades"
          helpText="Actualizamos periódicamente la oferta de alojamientos similares a los tuyos. Cada día del calendario se pinta según la demanda esperada para un huésped que llega esa fecha; haz click en un día con datos para ver la ocupación estimada del mercado."
        />
        {loading || !data ? (
          <Skeleton active paragraph={{ rows: 8 }} style={{ marginTop: 16 }} />
        ) : !data.habilitado ? (
          <Empty
            style={{ padding: 48 }}
            description="Esta sección estará disponible próximamente."
          />
        ) : (data.propiedades ?? []).length === 0 ? (
          <Empty
            style={{ padding: 48 }}
            description="Aún no hay datos de mercado para tus propiedades."
          />
        ) : (
          <div className="mercado-page">
            <Alert
              type="info"
              showIcon
              message="¿Qué estás viendo?"
              description="Medimos la oferta de alojamientos similares a los tuyos en tu zona. El calendario pinta cada fecha según cuánta demanda hay, y al abrir un día te muestra la ocupación estimada del mercado (qué porcentaje se arrienda para esa fecha) — así entiendes fácil cuándo hay más demanda. Las fechas sin pintar aún no tienen mediciones suficientes."
            />
            {opciones.length > 1 && (
              <Select
                showSearch
                optionFilterProp="label"
                style={{ maxWidth: 420, width: "100%" }}
                value={activa?.id}
                onChange={(v) => setPropId(v)}
                options={opciones}
              />
            )}
            {activa && (
              <MercadoCalendario
                key={activa.id}
                propiedadId={activa.id}
                nombre={formatPropertyName(activa.nombre)}
                refreshToken={refreshToken}
              />
            )}
          </div>
        )}
      </section>
    </Layout>
  );
};

export default MercadoClientePage;
